import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import Spinner from './Spinner';
import { backendUrl } from '../config';
import './ModulePage.css';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { isRTL as isRTLanguage } from '../utils/rtlUtils';
import { cacheUtils } from '../utils/cacheManager';
import { useModuleData } from '../hooks/useModuleData';
import { useWordGroupData } from '../hooks/useWordGroupData';
import { useWordsData } from '../hooks/useWordsData';

// Add a mapping from language names to codes for RTL detection
const LANGUAGE_NAME_TO_CODE = {
  Hebrew: 'he',
  Arabic: 'ar',
  Persian: 'fa',
  Urdu: 'ur',
  Yiddish: 'yi',
  Divehi: 'dv',
  // Add more as needed
};

function getLanguageCode(nameOrCode) {
  if (!nameOrCode) return '';
  // If already a code, return as is
  if (Object.values(LANGUAGE_NAME_TO_CODE).includes(nameOrCode.toLowerCase())) {
    return nameOrCode.toLowerCase();
  }
  // If a name, map to code
  return LANGUAGE_NAME_TO_CODE[nameOrCode] || nameOrCode.toLowerCase();
}

const StorySummaryPage = () => {
  const { t, currentLanguage } = useLanguage();
  const { id, groupId } = useParams();
  const { refetchUsage } = useOutletContext() || {};
  const navigate = useNavigate();

  const { moduleData, loading: moduleLoading } = useModuleData(id);
  const { groupData, loading: groupLoading } = useWordGroupData(id, groupId);
  const { words, loading: wordsLoading } = useWordsData(groupData?.id);
  const dataLoading = moduleLoading || groupLoading || wordsLoading;

  // Debug logging

  const [story, setStory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState('');
  const [feedback, setFeedback] = useState('');
  const [evaluating, setEvaluating] = useState(false);
  const [forceRefetch, setForceRefetch] = useState(0);
  const requestIdRef = useRef(0);
  const textareaRef = useRef(null);



  useEffect(() => {
    // Don't proceed if data is still loading or if we don't have the required data
    if (dataLoading || !moduleData || !groupData || !words) {
      return;
    }

    // Don't proceed if words are still loading
    if (wordsLoading) {
      // Clear error while loading to prevent stale error messages
      setError('');
      return;
    }

    // Don't proceed if words array is empty (means no words found)
    if (!words || words.length === 0) {
      setError(t('needAtLeastTenWords'));
      setLoading(false);
      return;
    }

    // Clear any previous error since we now have words
    setError('');



    const fetchStory = async () => {
      setLoading(true);
      setError('');
      const currentRequestId = ++requestIdRef.current;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;
        const res = await fetch(`${backendUrl}/gemini-generate-story-and-evaluate-summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            topic: groupData.name,
            fromLang: moduleData.from_lang,
            toLang: moduleData.to_lang,
            words: words.map(word => ({
              from_text: word.from_text,
              to_text: word.to_text
            })),
            interfaceLanguage: currentLanguage,
            accessToken,
          }),
        });
        if (!res.ok) {
          const data = await res.json();
          if (requestIdRef.current === currentRequestId) setError(data.error || t('failedToGenerateStory'));
          return;
        }
        const data = await res.json();
        if (requestIdRef.current === currentRequestId) {
          setStory(data.story);
          // Cache the story using cacheManager
          cacheUtils.cacheStory(moduleData.id, groupData.id, data.story);
          if (refetchUsage) refetchUsage();
        }
      } catch (error) {
        if (requestIdRef.current === currentRequestId) {
          console.error('Error fetching story:', error);
          setError(t('couldNotConnectToAI'));
        }
      } finally {
        if (requestIdRef.current === currentRequestId) setLoading(false);
      }
    };


    // At this point, words are loaded and we know there are words
    if (words.length >= 10) {
      // Check for cached story using cacheManager
      const cachedStory = cacheUtils.getCachedStory(moduleData.id, groupData.id);

      if (cachedStory) {
        setStory(cachedStory);
        setLoading(false);

        // Load cached summary and feedback
        const cachedSummary = cacheUtils.getCachedSummary(moduleData.id, groupData.id);
        const cachedFeedback = cacheUtils.getCachedFeedback(moduleData.id, groupData.id);

        if (cachedSummary) {
          setSummary(cachedSummary);
        }

        if (cachedFeedback) {
          setFeedback(cachedFeedback);
        }

        return;
      }
      fetchStory();
    } else {

      setError(t('needAtLeastTenWords'));
      setLoading(false);
    }
  }, [words, moduleData?.id, moduleData?.from_lang, moduleData?.to_lang, groupData?.name, currentLanguage, t, refetchUsage, groupId, forceRefetch, dataLoading, moduleData, groupData, wordsLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!summary.trim()) return;
    setEvaluating(true);
    setError('');
    const currentRequestId = ++requestIdRef.current;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      const res = await fetch(`${backendUrl}/gemini-generate-story-and-evaluate-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: groupData.name,
          fromLang: moduleData.from_lang,
          toLang: moduleData.to_lang,
          words: words.map(word => ({
            from_text: word.from_text,
            to_text: word.to_text
          })),
          summary,
          story,
          interfaceLanguage: currentLanguage,
          accessToken,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        if (requestIdRef.current === currentRequestId) setError(data.error || t('failedToEvaluateSummary'));
        return;
      }
      const data = await res.json();
      if (requestIdRef.current === currentRequestId) {
        setFeedback(data.feedback);

        // Cache the summary and feedback using cacheManager
        cacheUtils.cacheSummary(moduleData.id, groupData.id, summary);
        cacheUtils.cacheFeedback(moduleData.id, groupData.id, data.feedback);

        if (refetchUsage) refetchUsage();
      }
    } catch (error) {
      if (requestIdRef.current === currentRequestId) {
        console.error('Error evaluating summary:', error);
        setError(t('couldNotConnectToAI'));
      }
    } finally {
      if (requestIdRef.current === currentRequestId) setEvaluating(false);
    }
  };

  const handleSummaryChange = (e) => {
    const value = e.target.value;
    // Limit to 800 characters
    if (value.length <= 800) {
      setSummary(value);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      }
    }
  };

  // Use the language code for RTL detection
  const storyLangCode = getLanguageCode(moduleData?.to_lang);
  const isStoryRTL = isRTLanguage(storyLangCode);

  if (dataLoading) {
    return (
      <div className="module-page-container">
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <Spinner message={t('loading')} />
        </div>
      </div>
    );
  }

  if (!moduleData || !groupData) {
    return (
      <div className="module-page-container">
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{t('wordGroupNotFound')}</h2>
          <p>{t('wordGroupNotFoundMessage')}</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>{t('backToModules')}</button>
        </div>
      </div>
    );
  }

  if (words && words.length === 0) {
    return (
      <div className="module-page-container">
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{t('noWordsYet')}</h2>
          <p>{t('addYourFirstWords')}</p>
          <button className="btn btn-primary" onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)}>{t('backToWordGroup')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="module-page-container">
      <button onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)} className="btn btn-secondary btn-back">
        {t('backToWordGroup')}
      </button>
      <div className="card learn-topic-card">
        <h2>{t('story')}: {groupData.name}</h2>
        {loading ? (
          <Spinner message={t('generatingStory')} />
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            <div
              style={{
                whiteSpace: 'pre-line',
                marginBottom: '1.5rem',
                fontSize: '1.1rem',
                color: '#222',
                textAlign: isStoryRTL ? 'right' : 'left',
                direction: isStoryRTL ? 'rtl' : 'ltr',
              }}
            >
              {story}
            </div>
            {feedback ? (
              <div style={{ marginTop: '2rem' }}>
                <h4>{t('aiFeedback')}</h4>
                <div className="card" style={{ background: '#f8fff4', color: '#2c6e49', padding: '1rem', marginBottom: '1.5rem' }}>{feedback}</div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button className="btn btn-secondary" onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)}>{t('done')}</button>
                  <button className="btn btn-primary" onClick={() => {
                    // Clear all cached data using cacheManager
                    cacheUtils.clearStoryCache(moduleData.id, groupData.id);
                    cacheUtils.clearSummaryCache(moduleData.id, groupData.id);
                    cacheUtils.clearFeedbackCache(moduleData.id, groupData.id);

                    setFeedback('');
                    setSummary('');
                    setStory('');
                    setError('');
                    setLoading(true);

                    // Force a new story generation by incrementing forceRefetch
                    setForceRefetch(prev => prev + 1);
                  }}>{t('generateAnotherStory') || 'Generate Another Story'}</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ marginTop: '2rem' }}>
                <div className="form-group">
                  <label htmlFor="summary"><strong>{t('writeSummary')}</strong></label>
                  <textarea
                    id="summary"
                    value={summary}
                    onChange={handleSummaryChange}
                    className="form-control"
                    disabled={evaluating}
                    required
                    ref={textareaRef}
                    maxLength={800}
                    style={{ overflow: 'hidden' }}
                  />
                  <div style={{
                    textAlign: 'right',
                    fontSize: '0.85rem',
                    color: summary.length >= 750 ? '#d32f2f' : '#666',
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    {summary.length}/800 characters
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={evaluating || !summary.trim()}>
                  {evaluating ? t('evaluating') : t('submitSummary')}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StorySummaryPage; 